import { useEffect, useState } from 'react';
import {
  type PeriodicityTypes,
  usePatchCustomerPeriodicities,
  useProductPeriodicityMap,
} from '@hungryroot/components-osn';
import { useIdentify } from '@hungryroot/customer';
import { ButtonsGroup } from '@hungryroot/pho';

type OSNButtonGroupProps = {
  productId: number;
  onChange?: ({ option }: { option: PeriodicityTypes | null }) => void;
  className?: string;
};

const periodicityOptions = [
  {
    label: 'Often',
    value: 3,
  },
  {
    label: 'Sometimes',
    value: 2,
  },
  {
    label: 'Never',
    value: 1,
  },
];

export function OSNButtonGroup(props: OSNButtonGroupProps) {
  const { className, productId, onChange: passedOnChange } = props;

  const [periodicityValue, setPeriodicityValue] =
    useState<PeriodicityTypes | null>(null);
  const { customerData } = useIdentify();

  const {
    data: productPeriodicityMapData,
    isLoading: productPeriodicityMapIsLoading,
  } = useProductPeriodicityMap();

  const patchCustomerPeriodicities = usePatchCustomerPeriodicities();

  useEffect(() => {
    if (!productPeriodicityMapIsLoading) {
      setPeriodicityValue(productPeriodicityMapData.get(productId) ?? null);
    }
  }, [productId, productPeriodicityMapData, productPeriodicityMapIsLoading]);

  const onChange = ({
    prevValue,
    newValue,
  }: {
    prevValue: PeriodicityTypes | null;
    newValue: PeriodicityTypes | null;
  }) => {
    if (prevValue === newValue || newValue === null) {
      return;
    }

    if (customerData) {
      setPeriodicityValue(newValue);

      patchCustomerPeriodicities.mutate(
        {
          customerId: customerData.id,
          data: {
            periodicity: newValue,
            product: productId,
          },
        },
        {
          onError: () => {
            setPeriodicityValue(prevValue);
            passedOnChange?.({ option: prevValue });
          },
        }
      );
    }
  };

  return (
    <ButtonsGroup
      className={className}
      handleClick={(option) => {
        onChange({ prevValue: periodicityValue, newValue: option });
        passedOnChange?.({ option });
      }}
      selected={periodicityValue}
      options={periodicityOptions}
      labelVariant="H7"
    />
  );
}
